import { 
  Shield, 
  Cpu, 
  Globe, 
  BarChart3, 
  Search, 
  Lock, 
  Zap, 
  Activity, 
  Cloud, 
  Network,
  Users,
  Eye,
  Server,
  Terminal,
  ShieldCheck,
  Building2
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';

export type MarketKey = 
  | 'operadoras-isps' 
  | 'operadores-servicos-nuvem' 
  | 'redes-corporativas' 
  | 'ti-corporativa';

export type ScopeKey = 'local' | 'regional' | 'global';

export interface ServiceListItem {
  name: string;
  href: string;
  category: string;
  marketId: MarketKey;
  scope: ScopeKey;
}

export interface MarketStructure {
  id: MarketKey;
  title: string;
  desc: string;
  icon: LucideIcon;
  scopes: Record<ScopeKey, Array<{
    category: string;
    services: Array<{
      name: string;
      href: string;
    }>;
  }>>;
}

export const solutionsStructure: MarketStructure[] = [
  {
    id: 'operadoras-isps',
    title: 'Para operadores e ISPs',
    desc: 'Protección, observabilidad y rendimiento para redes que sustentan la conectividad.',
    icon: Network,
    scopes: {
      local: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      regional: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Network Edge & Core Observability for Providers', href: '/servicos/network-edge-core-observability-providers' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Advanced Edge & Core Observability for Transit Providers', href: '/servicos/advanced-edge-core-observability-transit' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' },
            { name: 'TE - End-to-End Observability for Providers', href: '/servicos/end-to-end-observability-providers' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      global: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Network Edge & Core Observability for Providers', href: '/servicos/network-edge-core-observability-providers' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Advanced Edge & Core Observability for Transit Providers', href: '/servicos/advanced-edge-core-observability-transit' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' },
            { name: 'TE - End-to-End Observability for Providers', href: '/servicos/end-to-end-observability-providers' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ]
    }
  },
  {
    id: 'operadores-servicos-nuvem',
    title: 'Para proveedores de servicios en la nube',
    desc: 'Protección y análisis para entornos digitales e infraestructuras distribuidas.',
    icon: Cloud,
    scopes: {
      local: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Cloud Performance Monitoring',
          services: [
            { name: 'TE - Cloud Analytics for Digital Enterprises', href: '/servicos/cloud-analytics-digital-enterprises' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Hybrid Observability for Digital Enterprises', href: '/servicos/hybrid-observability-digital-enterprises' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      regional: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Cloud Performance Monitoring',
          services: [
            { name: 'TE - Cloud Analytics for Digital Enterprises', href: '/servicos/cloud-analytics-digital-enterprises' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Hybrid Observability for Digital Enterprises', href: '/servicos/hybrid-observability-digital-enterprises' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      global: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Cloud Performance Monitoring',
          services: [
            { name: 'TE - Cloud Analytics for Digital Enterprises', href: '/servicos/cloud-analytics-digital-enterprises' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Hybrid Observability for Digital Enterprises', href: '/servicos/hybrid-observability-digital-enterprises' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ]
    }
  },
  {
    id: 'redes-corporativas',
    title: 'Para redes corporativas',
    desc: 'Confiabilidad, seguridad y operación especializada para TI de organizaciones.',
    icon: Building2,
    scopes: {
      local: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - Smart DNS for Corporations', href: '/servicos/smart-dns-corporations' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      regional: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for Corporations', href: '/servicos/smart-dns-corporations' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      global: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for Corporations', href: '/servicos/smart-dns-corporations' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ]
    }
  },
  {
    id: 'ti-corporativa',
    title: 'Para TI corporativa',
    desc: 'Eficiencia operativa y visibilidad total para equipos de TI y seguridad.',
    icon: Users,
    scopes: {
      local: [
        {
          category: 'Professional Services',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' }
          ]
        }
      ],
      regional: [
        {
          category: 'Professional Services',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' }
          ]
        }
      ],
      global: [
        {
          category: 'Professional Services',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' }
          ]
        }
      ]
    }
  }
];

export interface ServiceData {
  title: string;
  slug: string;
  sku: string;
  subtitle: string;
  tagline: string;
  description: string;
  primaryMarket: MarketKey;
  relatedMarkets: MarketKey[];
  technicalCategories: string[];
  targetAudience: {
    idealClient: string;
    useCases: string[];
  };
  benefits: string[]; // Benefícios principais
  competitiveAdvantages: string[]; // Diferenciais competitivos
  uniqueValue: string; // Valor único
  keyFeatures: string[]; // Funcionalidades-chave
  technicalDetails: string[]; // Detalhes técnicos importantes
  commercialModels: string[]; // Modelos de comercialização
  image?: string;
}

export const servicesData: Record<string, ServiceData> = {
  'advanced-edge-core-observability-b2c': {
    title: "TE - Advanced Edge & Core Observability for B2C Providers",
    slug: 'advanced-edge-core-observability-b2c',
    sku: "B2C-TECH-MINDVIEW",
    subtitle: "Aproveche la información de los suscriptores en tiempo real para impulsar los ingresos",
    tagline: null,
    description: "Esta solución TechEnabler se basa en la composición de la tecnología Kentik con la ingeniería TechEnabler y se personaliza específicamente para proveedores B2C que requieren observabilidad avanzada de borde y núcleo. Combina el análisis del tráfico en tiempo real con información sobre la experiencia del usuario, lo que permite a los proveedores de servicios optimizar la calidad de la experiencia para los clientes finales.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability"],
    targetAudience: {
      idealClient: "proveedores B2C, ISP residenciales, operadores móviles enfocados en clientes finales y proveedores de servicios digitales.",
      useCases: ["análisis de la experiencia del usuario individual, optimización de servicios CDN/OTT, gestión de programas de tarifa cero, planificación de ofertas personalizadas."]
    },
    benefits: ["visibilidad granular por suscriptor, información para la retención, optimización de ingresos, experiencia digital mejorada."],
    competitiveAdvantages: [],
    uniqueValue: "reducción de MTTR (resolución rápida de problemas que afectan la experiencia del usuario), aumento de NPS, crecimiento de ingresos con servicios personalizados, reducción de churn.",
    keyFeatures: ["Inteligencia de suscriptores, análisis de CDN y OTT, pruebas sintéticas."],
    technicalDetails: ["procesamiento masivo, análisis multidimensional granular, retención de datos forenses, API abiertas a través de REST-API. Servicio basado en la plataforma Kentik, SOC TechEnabler e Ingeniería de productos TechEnabler."],
    commercialModels: ["OPEX: SaaS, como servicio."],
    image: "/technabler-solucao-network-edge-&-core-observability-for-providers.webp"
  },
  'advanced-edge-core-observability-transit': {
    title: "TE - Advanced Edge & Core Observability for Transit Providers",
    slug: 'advanced-edge-core-observability-transit',
    sku: "T-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "Esta solución TechEnabler fue diseñada a partir de la composición de las tecnologías Kentik y Corero con ingeniería TechEnabler. Fue desarrollada específicamente para proveedores de tránsito IP que requieren visibilidad completa y análisis avanzadas de su infraestructura troncal y conexiones de tránsito. La solución le permite optimizar las operaciones a través de la plataforma de inteligencia de red con capacidades avanzadas de interconexión y emparejamiento, lo que le permite construir casos basados en datos de interconexión para reducir costos y aumentar el rendimiento.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability"],
    targetAudience: {
      idealClient: "proveedores de tránsito, redes de nivel 1, operadores de red troncal global, proveedores de conectividad internacional.",
      useCases: ["optimización de costos de tránsito, análisis de peering e interconexión, inteligencia de mercado, planificación de capacidad, monitoreo de SLA."]
    },
    benefits: ["reducción de costos de tránsito, optimización de interconexiones, visibilidad global de la red troncal, inteligencia competitiva."],
    competitiveAdvantages: [],
    uniqueValue: "reducción de costos, crecimiento de ingresos, IA integrada.",
    keyFeatures: ["Análisis de peering con datos de peering de base de datos integrados, Network Cost Intelligence, monitoreo de BGP, pruebas de sintéticos."],
    technicalDetails: ["NetFlow, sFlow, J-Flow, IPFIX, NetStream, compatibilidad multiprotocolo con Cflow, retención de datos forenses, alta capacidad, sin límite en el número de interfaces. Servicio basado en la plataforma Kentik, SOC TechEnabler e Ingeniería de productos TechEnabler."],
    commercialModels: ["OPEX: SaaS, as a Service."],
    image: "/technabler-solucao-network-edge-&-core-observability-for-providers.webp"
  },
  'cloud-analytics-digital-enterprises': {
    title: "TE - Cloud Analytics for Digital Enterprises",
    slug: 'cloud-analytics-digital-enterprises',
    sku: "TECH-ANALYTICS",
    subtitle: null,
    tagline: null,
    description: "Esta solución TechEnabler combina la plataforma Kentik con servicios especializados que ofrecen análisis de performance en la nube y análisis CDN/OTT, lo que representa una solución para los proveedores de servicios digitales que necesitan visibilidad de las estructuras locales y en la nube. Permite el mapeo de las cadenas de suministro digitales de los clientes a más de 350 aplicaciones OTT y CDN.",
    primaryMarket: 'operadores-servicos-nuvem',
    relatedMarkets: ["ti-corporativa"],
    technicalCategories: ["Cloud Performance Monitoring"],
    image: "/technabler-solucao-cloud-analytics-for-digital-enterprises.webp",
    targetAudience: {
      idealClient: "empresas digitales que operan en entornos de nube, centros de datos regionales, proveedores locales de nube, empresas digitales.",
      useCases: ["optimización de CDN y OTT, monitoreo en la nube, análisis de costos, inteligencia de marketing."]
    },
    benefits: ["visibilidad unificada de la nube, análisis OTT avanzado, inteligencia empresarial."],
    competitiveAdvantages: ["escala masiva, IA integrada, experiencia local."],
    uniqueValue: "optimización de costos en la nube, reducción de la complejidad.",
    keyFeatures: ["análisis avanzado de CDN (mapea la cadena de suministro en más de 350 CDN), monitoreo en la nube, análisis de performance."],
    technicalDetails: ["soporte multinube, análisis sin DPI, análisis multidimensional."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'end-to-end-observability-providers': {
    title: "TE - End-to-End Observability for Providers",
    slug: 'end-to-end-observability-providers',
    sku: "H-TECH-MINDFULL",
    subtitle: null,
    tagline: null,
    description: "Esta solución TechEnabler combina los sistemas Kentik, Corero y Unryo con los servicios expertos de TechEnabler que ofrecen segmentación de redes de extremo a extremo y visibilidad y supervisión proactivas de la experiencia del usuario. La solución opera en un escenario híbrido y multinube. Especialmente desarrollada para proveedores que enfrentan la densificación de la complejidad de su red y al crecimiento del nivel de exigencia de sus usuarios.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Next Generation Observability"],
    image: "/technabler-solucao-end-to-end-observability-for-providers.webp",
    targetAudience: {
      idealClient: "grandes proveedores de servicios cuya red está evolucionando y modernizándose.",
      useCases: ["optimización operativa, modernización de la infraestructura, monitoreo proactivo, análisis avanzado."]
    },
    benefits: ["observabilidad de extremo a extremo, monitoreo proactivo, eficiencia operativa, visibilidad estratégica."],
    competitiveAdvantages: ["plataforma única, IA integrada, experiencia local, SOCaaS especializado."],
    uniqueValue: "reducción de costos, SLA mejorados, eficiencia del equipo, ROI comprobado.",
    keyFeatures: ["monitoreo multicapa, automatización avanzada, análisis integrado."],
    technicalDetails: ["capacidad masiva, soporte multiprotocolo, escalabilidad ilimitada."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'full-stack-observability-platform': {
    title: "TE - Full Stack Observability Platform",
    slug: 'full-stack-observability-platform',
    sku: "TECH-MINDFULL",
    subtitle: null,
    tagline: null,
    description: "Esta solución TechEnabler combina las plataformas Kentik, Corero y Unryo con servicios especializados para ofrecer inteligencia total y toda la telemetría de red unificada. Utilizando el aprendizaje automático a gran escala, identifica anomalías en tiempo real, ofreciendo servicio dentro del concepto de SDOM (Smart Data Observability per Metrics) y asegurando así una visibilidad unificada de los dispositivos en la nube y en las instalaciones.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Next Generation Observability","Cloud Performance Monitoring"],
    image: "/technabler-solucao-full-stack-observability-platform.webp",
    targetAudience: {
      idealClient: "proveedores de nube digital, redes corporativas y proveedores de servicios en general.",
      useCases: ["observabilidad de extremo a extremo, monitoreo multicapa, consolidación de herramientas y sistemas."]
    },
    benefits: ["Visibilidad unificada, IA masiva, consolidación de costos, observabilidad multiservicio."],
    competitiveAdvantages: ["visión multinivel y multiservicio unificada."],
    uniqueValue: "eficiencia operativa, reducción de MTTR, visión estratégica de sistemas y servicios.",
    keyFeatures: ["enriquecimiento de datos, integración de IA, monitoreo multicapa, consolidación general de datos a través de IA."],
    technicalDetails: ["soporte multiprotocolo, escalabilidad ilimitada, análisis multidimensional, conjunto completo de API."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'hybrid-observability-digital-enterprises': {
    title: "TE - Hybrid Observability for Digital Enterprises",
    slug: 'hybrid-observability-digital-enterprises',
    sku: "H-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "Esta solución TechEnabler combina la plataforma Kentik con servicios expertos para ofrecer una observabilidad completa en entornos híbridos de múltiples nubes. Permite una visibilidad unificada en redes locales híbridas y multinube y centros de datos, proporcionando pruebas sintéticas y análisis de flujo de tráfico en un solo sistema. La solución tiene como objetivo servir a corporaciones digitales de todos los tamaños.",
    primaryMarket: 'operadores-servicos-nuvem',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability","Cloud Performance Monitoring"],
    image: "/technabler-solucao-hybrid-observability-for-digital-enterprises.webp",
    targetAudience: {
      idealClient: "empresas digitales, redes corporativas, empresas con infraestructura híbrida multinube.",
      useCases: ["monitoreo híbrido y multinube, optimización de costos en la nube, monitoreo de experiencia digital, resolución avanzada de problemas."]
    },
    benefits: ["Visibilidad unificada, reducción de costos, eficiencia operativa, consolidación de herramientas y sistemas."],
    competitiveAdvantages: ["información proporcionada por la IA, soporte local."],
    uniqueValue: "reducción de MTTR, alta productividad, visibilidad estratégica.",
    keyFeatures: ["monitorización híbrida y multinube, monitorización de la performance, monitorización multifuncional (infraestructura, CDNs, aplicaciones multinube y SaaS)."],
    technicalDetails: ["multiprotocolo, capacidades de IA, API abiertas."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'integration-solution-development': {
    title: "TE - Integration and Solution Development",
    slug: 'integration-solution-development',
    sku: "TECH-SERVICES",
    subtitle: null,
    tagline: null,
    description: "Estos son servicios especializados presentes en el portafolio de TechEnabler para ofrecer a nuestros clientes comodidad y tranquilidad en la implementación de soluciones de protección y observabilidad para sus redes. TechEnabler domina por completo todos los escenarios de integración posibles de las soluciones tecnológicas que forman parte de sus proyectos.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas","ti-corporativa"],
    technicalCategories: ["Professional Services & Integration"],
    image: "/technabler-solucao-integration-and-solution-development.webp",
    targetAudience: {
      idealClient: "empresas digitales que operan en entornos de nube, centros de datos regionales, proveedores de nube locales, empresas digitales.",
      useCases: ["integración tecnológica, desarrollo a medida, cumplimiento normativo."]
    },
    benefits: ["experiencia consolidada, integración de extremo a extremo, asociaciones estratégicas."],
    competitiveAdvantages: ["SOC especializado, personalización completa, integraciones locales, soporte multilingüe."],
    uniqueValue: "optimización de la operación, control de la implementación del proyecto, time-to-market.",
    keyFeatures: ["disponibilidad de API, desarrollo personalizado, portal multiusuario, informes detallados."],
    technicalDetails: ["webhooks para notificaciones, consultas SQL abiertas, implementaciones híbridas."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'local-scrubbing-as-a-service-lsaas': {
    title: "TE - Local Scrubbing as a Service - LSaaS",
    slug: 'local-scrubbing-as-a-service-lsaas',
    sku: "ISP-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "La solución LSaaS es un modelo de negocio desarrollado por TechEnabler para hacer más accesible una tecnología premium de defensa DDoS. Establecimos, en puntos estratégicos de Brasil, centros de mitigación de alta capacidad. Hoy en día, los clústeres de mitigación están implementados en São Paulo, Río de Janeiro y Fortaleza; otros están en análisis para ser instalados. Nuestros clientes se conectan directamente a estos centros, enviando su tráfico y recibiéndolo de vuelta, limpio y libre de ataques. Debido a que es una conexión directa, funciona como si perteneciera a su columna vertebral. En la práctica, es un nuevo elemento de red conectado localmente a su enrutador. Este es un modelo de negocio en formato Opex, con pagos mensuales.\nEl cliente puede comenzar poco a poco, asignando una fracción de la capacidad de mitigación disponible y aumentando esta reserva a medida que crece su red. Así, no hay desperdicio de inversión y el cliente paga solo por los recursos que utiliza.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-local-scrubbing-as-a-service-lsaas.webp",
    targetAudience: {
      idealClient: "Empresas medianas que tienen capacidad de enlace y desean ofrecer una protección superior. Corporaciones de TI y empresas digitales que no necesitan invertir en la compra de estos equipos.",
      useCases: ["Bloquee los ataques DDoS."]
    },
    benefits: ["sin latencia, sin protección Always-On, sin fugas, sin averías."],
    competitiveAdvantages: ["solución anti-DDoS local premium con gran costo-beneficio."],
    uniqueValue: "podemos implementar portales de clientes (sub-tenants) para la creación de productos para la generación de ingresos.",
    keyFeatures: ["El portal de acceso de clientes proporciona todos los datos de ataque y se actualiza en tiempo real. La solución incluye un servicio SOC para la parametrización de reglas de bloqueo y monitorización en caso de ataques."],
    technicalDetails: ["El cliente debe solicitar una conexión cruzada en el centro de datos. El tráfico dirigido al centro de depuración lo decide el cliente."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'network-edge-core-observability-providers': {
    title: "TE - Network Edge & Core Observability for Providers",
    slug: 'network-edge-core-observability-providers',
    sku: "ISP-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "Esta solución de TechEnabler agrega inteligencia a las capacidades de observabilidad. Al combinar la tecnología de Kentik con la ingeniería de TechEnabler, es posible ofrecer un sistema de observabilidad e inteligencia artificial basado en los desafíos que enfrentan los ISP. Por lo tanto, contamos con una plataforma de inteligencia de red dedicada específicamente a los equipos de infraestructura, lo que permite una visibilidad completa de las redes híbridas y multinube. La solución unifica y correlaciona los datos en la nube, los dispositivos, las transmisiones y los datos sintéticos en una sola plataforma.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability"],
    image: "/technabler-solucao-network-edge-&-core-observability-for-providers.webp",
    targetAudience: {
      idealClient: "proveedores (ISP), operadores de telecomunicaciones, empresas Tier 1 y Tier 2.",
      useCases: ["análisis de tráfico de red, planificación de capacidad, emparejamiento y optimización de interconexión, análisis de costos de conectividad y tránsito, monitoreo proactivo de red, detección y mitigación de DDoS."]
    },
    benefits: ["velocidad de implementación más rápida que las herramientas tradicionales, escalabilidad masiva con flujos ilimitados, interfaz intuitiva con módulo de IA, soporte local a través de SOC TechEnabler."],
    competitiveAdvantages: ["100% en la nube, aceleración del trabajo con IA integrada."],
    uniqueValue: "costos de conectividad reducidos, visibilidad de 360 grados para decisiones proactivas. Ahorro de tiempo en diagnóstico y combate, anticipación de fallas, generación de ingresos a través del portal multi-tenant TechEnabler.",
    keyFeatures: ["Compatible con NetFlow, sFlow, IPFIX, SNMP y BGP; enriquecimiento de datos con geolocalización, emparejamiento de bases de datos y datos comerciales, aprendizaje automático, API abiertas, paneles personalizados."],
    technicalDetails: ["Servicio basado en la plataforma Kentik e Habilitador técnico SOC."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'managed-traffic-analysis': {
    title: "TE - Managed Traffic Analysis",
    slug: 'managed-traffic-analysis',
    sku: "TECH-TRAFFIC MANAGER",
    subtitle: null,
    tagline: null,
    description: "Esta solución TechEnabler combina la plataforma Kentik con los servicios SOCaaS de TechEnabler para ofrecer un análisis continuo e inteligente del tráfico de red. Permite la planificación de la capacidad de la red, la optimización del emparejamiento y la interconexión, así como el análisis de OTT / CDN a través de un menú completo de análisis de red y parámetros de diagnóstico. El servicio incluye el TechEnabler del Centro de Operaciones de Servicios SOCaaS para soporte 24x7.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Next Generation Observability"],
    image: "/technabler-solucao-managed-traffic-analysis.webp",
    targetAudience: {
      idealClient: "proveedores de ISP, operadores de telecomunicaciones y empresas digitales que necesitan un análisis continuo del tráfico con soporte especializado.",
      useCases: ["análisis de tráfico gestionado, optimización de costos operativos, apoyo a la toma de decisiones estratégicas, análisis de CDN y OTT, planificación de la capacidad gestionada."]
    },
    benefits: ["SOC dedicado de servicio completo y administrado, inteligencia operativa, soporte local experto."],
    competitiveAdvantages: ["integración con múltiples tecnologías, experiencia consolidada."],
    uniqueValue: "reducción de costos, optimización del tiempo, visibilidad estratégica.",
    keyFeatures: ["Análisis de flujo completo L3/L4 con planificación de capacidad, monitoreo sintético para validación de servicios, automatización de procesos, análisis de experiencia digital."],
    technicalDetails: ["soporte multiprotocolo, API abiertas, escalabilidad masiva."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'mssp-services': {
    title: "TE - MSSP Services",
    slug: 'mssp-services',
    sku: "TECH-MSSP",
    subtitle: null,
    tagline: null,
    description: "Estos servicios combinan la experiencia del Centro de Operaciones de Servicios SOCaaS con tecnologías avanzadas para proporcionar protección y observabilidad DDoS de clase mundial. Integramos servicios SOC de clase mundial en modo 24x7x365 a través de asociaciones estratégicas con proveedores de tecnología, ofreciendo protección y observabilidad de múltiples usuarios.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-mssp-services.webp",
    targetAudience: {
      idealClient: "ISP y empresas digitales que necesitan un monitoreo intensivo de sus mecanismos de protección.",
      useCases: ["Protección siempre activa, servicios gestionados al proveedor, portal multi usuarios."]
    },
    benefits: ["protección sin tiempo de inactividad, monitoreo y soporte constantes, escalabilidad, visibilidad completa."],
    competitiveAdvantages: ["nuestro equipo cuenta con certificación MSSP, brindando el más alto grado de conocimiento y calidad en la prestación de servicios."],
    uniqueValue: "reducción del MTTR y optimización del TCO.",
    keyFeatures: ["SecureWatch Analytics, portal multiusuario, aprovisionamiento automatizado."],
    technicalDetails: ["capacidad disponible muy alta (decenas de terabits por segundo)."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'no-appliance-detection': {
    title: "TE - No Appliance Detection",
    slug: 'no-appliance-detection',
    sku: "NA-TECH-DETECTOR",
    subtitle: "Proteja su red sin necesidad de inversión",
    tagline: null,
    description: "La solución No Appliance Detection de TechEnabler proporciona un monitoreo continuo del tráfico de red mediante el análisis de los flujos generados por los enrutadores de red del cliente. Así, identifica y clasifica automáticamente los ataques DDoS en tiempo real, permitiendo al cliente tomar las acciones necesarias para evitar daños en su red. La solución se implementa como un servicio, incluyendo consultoría, diseño e implementación de TechEnabler, sin necesidad de inversión Capex por parte del cliente.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-no-appliance-detection.webp",
    targetAudience: {
      idealClient: "proveedores de servicios de todos los tamaños, y medianas y grandes empresas que necesitan proteger sus enlaces y aplicaciones críticas contra ataques DDoS, sin invertir en una estructura dedicada.",
      useCases: ["detección de ataques DDoS, monitoreo de tráfico anómalo, informes de amenazas y soporte al equipo del Centro de Operaciones de Seguridad (SOC)."]
    },
    benefits: ["No presenta latencia notable, protección Always-On con detección en segundos, paneles en tiempo real y alertas proactivas."],
    competitiveAdvantages: ["plataforma escalable y flexible, retención de datos sin procesar durante largos períodos, integración de múltiples usuarios, API para automatización y orquestación, informes detallados."],
    uniqueValue: "ahorro de tiempo en diagnóstico, anticipación de fallas, generación de ingresos a través del portal multi-tenant, mayor seguridad sin necesidad de activos físicos.",
    keyFeatures: ["Compatible con NetFlow, sFlow, IPFIX, SNMP y BGP; incluido el monitoreo de muestreo y la recopilación de datos en entornos físicos, virtuales y en la nube."],
    technicalDetails: ["Servicio basado en la plataforma Kentik e Habilitador técnico SOC."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'no-appliance-protection': {
    title: "TE - No Appliance Protection",
    slug: 'no-appliance-protection',
    sku: "NA-TECH-PROTECTOR",
    subtitle: "Transforme sus enrutadores en su primera línea de defensa",
    tagline: null,
    description: "La solución No Appliance Protection de TechEnabler proporciona monitoreo continuo y mitigación del tráfico de red mediante el análisis de los flujos generados por los enrutadores de red del cliente. Identifica, clasifica y mitiga automáticamente los ataques DDoS en tiempo real mediante la exportación de políticas directamente a los enrutadores de los clientes. La solución se implementa como un servicio, que incluye consultoría, diseño e implementación de TechEnabler, sin necesidad de inversión de Capex por parte del cliente.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-no-appliance-protection.webp",
    targetAudience: {
      idealClient: "proveedores de servicios de todos los tamaños, medianas y grandes empresas que necesitan proteger sus enlaces y aplicaciones críticas contra ataques DDoS a través de acciones de mitigación automáticas, sin inversión en una estructura dedicada.",
      useCases: ["detección automática y combate de ataques DDoS, monitoreo de tráfico anómalo, generación de informes de amenazas y soporte al equipo del Centro de Operaciones de Seguridad (SOC)."]
    },
    benefits: ["No presenta latencia notable, protección siempre activa con detección y combate en segundos, paneles en tiempo real y alertas proactivas."],
    competitiveAdvantages: ["plataforma escalable y flexible, retención de datos sin procesar durante largos períodos, integración de múltiples usuarios, API para automatización y orquestación, informes detallados."],
    uniqueValue: "ahorro de tiempo en diagnóstico y combate, anticipación de fallas, generación de ingresos a través del portal multi-tenant, mayor seguridad sin necesidad de activos físicos.",
    keyFeatures: ["Compatible con NetFlow, sFlow, IPFIX, SNMP y BGP; incluido el monitoreo de muestreo y la recopilación de datos en entornos físicos, virtuales y en la nube."],
    technicalDetails: ["Servicio basado en la plataforma Kentik e Habilitador técnico SOC."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'smart-dns-corporations': {
    title: "TE - Smart DNS for Corporations",
    slug: 'smart-dns-corporations',
    sku: "TECH-SMART-DNS-CORP",
    subtitle: null,
    tagline: null,
    description: "Este servicio, desarrollado por TechEnabler, utiliza la tecnología DNS segura de Planisys, componiendo una solución adaptada a las necesidades de las empresas para proteger el tráfico saliente de su red, a través de PDNS y RPZ (Response Policy Zone).",
    primaryMarket: 'redes-corporativas',
    relatedMarkets: [],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-smart-dns-for-corporations.webp",
    targetAudience: {
      idealClient: "empresas de todos los tamaños que quieren tener una estrategia de navegación segura, impidiendo el acceso a sitios web maliciosos con procesos actualizados por bases de datos globales de ciberseguridad.",
      useCases: ["protección de los usuarios contra enlaces peligrosos; protección de sistemas IoT; protección contra phishing y malware; resolvers RPZ gestionados por el cliente; política de bloqueo; lista negra DNS; cumplimiento de bloqueos ANATEL (de forma nativa)."]
    },
    benefits: ["servicio transparente para el usuario; configuración sin interrupción de los servicios; software actualizado permanentemente; sistema de gestión centralizado; instalación dedicada por perfil de protección."],
    competitiveAdvantages: ["no es necesario instalar aplicaciones; sistema de gestión web sencillo; gestión de alto nivel soportada por TechEnabler con informes y análisis."],
    uniqueValue: "operación sencilla sin necesidad de experiencia del personal; venta de soluciones de seguridad a todos los usuarios de la red de la empresa.",
    keyFeatures: ["PDNS/RPZ con inteligencia de amenazas."],
    technicalDetails: ["El software RPZ se instala junto con el DNS. Se instala una RPZ para cada perfil de protección definido por el cliente. Instalación en la nube sin impacto en la red del cliente."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'smart-dns-isps': {
    title: "TE - Smart DNS for ISPs",
    slug: 'smart-dns-isps',
    sku: "TECH-SMART-DNS-ISP",
    subtitle: null,
    tagline: null,
    description: "Este servicio, desarrollado por TechEnabler, utiliza la tecnología DNS segura de Planisys, componiendo una solución adaptada a las necesidades de los ISP para proteger el tráfico saliente tanto de su red como de la red de sus clientes a través de PDNS y RPZ (Response Policy Zone).",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Network Defense"],
    targetAudience: {
      idealClient: "ISPs y operadores de telecomunicaciones",
      useCases: ["protección de usuarios contra enlaces peligrosos; protección de sistemas IoT; protección contra phishing y malware; resolvers RPZ gestionados por el ISP; política de bloqueo; lista negra DNS; cumplimiento de bloqueos ANATEL (nativo); reventa del servicio de protección a clientes finales del ISP."]
    },
    benefits: ["servicio transparente para el usuario; configuración sin interrupción de los servicios; software actualizado permanentemente; sistema de gestión centralizada personalizable."],
    competitiveAdvantages: ["sistema multi usuarios; cada instalación DNS tiene su propio RPZ; configuración mínima en la red existente; apuntando únicamente al router a la dirección RPZ."],
    uniqueValue: "operación sencilla sin necesidad de la experiencia del personal; venta de una solución de seguridad a los clientes finales del ISP (solución monetizable).",
    keyFeatures: ["PDNS/RPZ con inteligencia de amenazas."],
    technicalDetails: ["El software RPZ se instala junto con el DNS. Se instala una RPZ para cada perfil de protección definido por el cliente. Instalación local, en la nube o híbrida."],
    commercialModels: ["OPEX: SaaS, as a Service."],
    image: "/technabler-solucao-smart-dns-isps-arquitetura.webp"
  },
  'soc-services': {
    title: "TE - SOC Services",
    slug: 'soc-services',
    sku: "TECH-SOC",
    subtitle: null,
    tagline: null,
    description: "TechEnabler ha desarrollado un nuevo modelo de negocio en Service Operations Center con el objetivo de ofrecer visibilidad y protección proactiva mediante diversas tecnologías integradas. Somos pioneros en el modelo SOC as a Service, ofreciendo observabilidad completa de la red a través de técnicas de AIOps.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-soc-services.webp",
    targetAudience: {
      idealClient: "ISPs, operadoras y empresas digitales que necesiten externalizar el trabajo del SOC, o contar con un equipo de alto nivel que respalde a su equipo local.",
      useCases: ["monitoreo proactivo 24x7, detección de ataques DDoS, análisis de comportamiento del tráfico."]
    },
    benefits: ["protección sin tiempo de inactividad, Always-On, con soporte local en español."],
    competitiveAdvantages: ["Soporte de servicio de SOC World-Class 24x7x365."],
    uniqueValue: "capacidad e implementación de un Portal de Monitoreo de Incidentes para la infraestructura del cliente y para los clientes finales, generando ingresos en el modelo multi-tenant.",
    keyFeatures: ["sistema analítico con IA y portal multiusuario (multi-tenant)."],
    technicalDetails: ["servicio prestado a través de la combinación de tecnologías Kentik, Corero, Unryo y SOC TechEnabler."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'cloud-defense': {
    title: "TE - Cloud Defense",
    slug: 'cloud-defense',
    sku: "TECHCLOUD",
    subtitle: "El servicio anti-DDoS en la nube de TechEnabler",
    tagline: null,
    description: "El servicio TechCloud es el Cloud Scrubbing de TechEnabler. Consiste en una nube de mitigación de ataques DDoS con calidad premium en todos sus elementos, desde los proveedores de tránsito hasta los equipos de enrutamiento y el sistema de mitigación. Este scrubbing se puede conectar mediante el ASN 274830 y, a través de alianzas locales, podemos realizar entregas en casi todo el territorio latinoamericano.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-cloud-defense.webp",
    targetAudience: {
      idealClient: "empresas de cualquier tamaño que deseen contratar una protección en la nube.",
      useCases: ["bloquear ataques DDoS sin impactar la infraestructura propia."]
    },
    benefits: ["mayor poder y eficiencia de mitigación, portal de análisis en tiempo real, acceso a una amplia red de transporte, defensa desde la capa tres hasta la capa siete de la red."],
    competitiveAdvantages: ["podemos ofrecer un detector para automatizar el proceso de redireccionamiento de la solución."],
    uniqueValue: "mayor eficiencia de mitigación y soporte premium TechEnabler 24x7.",
    keyFeatures: ["Protección Always-On; Protección On-Demand."],
    technicalDetails: ["Incluir el AS o AS-Cone en RADb, con liberación en pocos minutos; determinar la forma de entrega del tráfico limpio: PNI, IX o GRE."],
    commercialModels: ["Servicio de Protección."]
  }
};

export interface MarketData {
  key: MarketKey;
  title: string;
  slug: string;
  description: string; // Short description
  fullDescription: string; // Detailed description for home/landing pages
  challenges: string[];
}

export const marketsData: MarketData[] = [
  {
    key: 'operadoras-isps' as MarketKey,
    title: 'Para operadores e ISPs',
    slug: '/mercados/operadoras-isps',
    description: 'Seguridad, observabilidad y rendimiento para las redes que sustentan la conectividad.',
    fullDescription: 'Para operadores y proveedores de servicios de Internet, la disponibilidad y seguridad de la red son su propio negocio. TechEnabler ofrece soluciones que protegen la infraestructura contra ataques, automatizan la mitigación de problemas y amplían la visibilidad operativa.',
    challenges: [
      'Protección contra ataques masivos DDoS',
      'Optimización de peering y tránsito IP',
      'Monetización de servicios de seguridad (VAS)',
      'Observabilidad de la red troncal o troncales globales'
    ]
  },
  {
    key: 'operadores-servicos-nuvem' as MarketKey,
    title: 'Para proveedores de servicios en la nube',
    slug: '/mercados/operadores-servicos-nuvem',
    description: 'Protección y análisis para entornos digitales e infraestructuras distribuidas.',
    fullDescription: 'Los operadores de nube y centros de datos requieren resistencia absoluta y lecturas precisas del uso de recursos. Nuestras soluciones, análisis avanzado y observabilidad completa garantizan que el entorno digital siempre esté protegido.',
    challenges: [
      'Visibilidad unificada en múltiples nubes',
      'Rendimiento de aplicaciones OTT/CDN',
      'Seguridad de la infraestructura escalable',
      'Monitoreo de experiencia digital'
    ]
  },
  {
    key: 'redes-corporativas' as MarketKey,
    title: 'Para redes corporativas',
    slug: '/mercados/redes-corporativas',
    description: 'Confiabilidad, seguridad y operación especializada para TI de grandes organizaciones.',
    fullDescription: 'Empresas de distintos sectores dependen de redes corporativas robustas para sostener sus operaciones. Ayudamos a TI comercial a implementar defensa basadas en DNS, integrar y anticiparse a todos fallos operacionales de escala mundial.',
    challenges: [
      'Seguridad del tráfico saliente (DNS)',
      'Protección de activos críticos sin hardware Capex',
      'Observabilidad en redes híbridas',
      'Monitoreo de dispositivos IoT'
    ]
  },
  {
    key: 'ti-corporativa' as MarketKey,
    title: 'Para TI corporativa',
    slug: '/mercados/ti-corporativa',
    description: 'Eficiencia operativa y visibilidad total para los equipos de TI y seguridad.',
    fullDescription: 'Los de TI y Seguridad necesitan herramientas que automatizan el diagnóstico y proporcionan visibilidad del rendimiento de las instalaciones e infraestructuras de soluciones de corporación en toda la región latinoamericana entregando capacidad técnica.',
    challenges: [
      'Reducción de MTTR en incidentes',
      'Cumplimiento y auditoría de tráfico',
      'Simplificación en gestión de herramientas',
      'Detección proactiva de anomalías o fallos'
    ]
  }
];
